import type { ReactElement } from 'react';
import React, { useState } from 'react';

import type { ReminderClaimNotificationModel, UpdateNotification } from '~/components/ClaimNotificationsCard/types';
import CliveRecommendationPanel from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanel';
import CliveRecommendationPanelButton from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanelButton';
import { CliveSummaryCard } from '~/components/Clive/SummaryCard/CliveSummaryCard';
import CliveInvoiceActionContainer from '~/components/Documents/CliveInvoiceActionContainer';
import RetryFailedUploadDialog from '~/components/Documents/UploadDocumentsCard/RetryFailedUploadDialog';
import type { DocumentWithCliveInfo } from '~/components/Documents/utils/cliveDocumentHelpers';
import { getClivePanelContent } from '~/components/Documents/utils/cliveDocumentHelpers';
import { getFailedUploadingDocumentFromStoredFile } from '~/components/Documents/utils/uploadingDocumentHelpers';
import { changeClaimNotificationDismissState } from '~/components/Notifications/utils';
import type { CliveAction } from '~/components/types/claim-document-types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { reportAxiosError } from '~/Utils';

// Document type matching claim.documents (for CliveInvoiceActionContainer)
type ClaimDocument = NonNullable<ClaimPolymorph['documents']>[number];

interface CliveReminderNotificationContentProps {
  reminder: ReminderClaimNotificationModel;
  claim: ClaimPolymorph;
  onClose: () => void;
  onUpdate: () => void;
  onUpdateNotification: UpdateNotification;
}

/**
 * Component that handles Clive-specific logic for reminder notifications.
 * Displays Clive processing status banner with document summary and classification options.
 */
function CliveReminderNotificationContent({
  reminder,
  claim,
  onClose,
  onUpdate,
  onUpdateNotification,
}: CliveReminderNotificationContentProps): ReactElement | null {
  const [showEditDocument, setShowEditDocument] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [activeInvoiceAction, setActiveInvoiceAction] = useState<CliveAction | null>(null);

  // Don't render anything if no document/stored_file
  if (!reminder.document && !reminder.stored_file) {
    return null;
  }

  const handleUploadSuccess = async () => {
    setShowEditDocument(false);

    // Auto-dismiss the notification after successful upload for immediate UI feedback
    try {
      await changeClaimNotificationDismissState(
        reminder,
        () => {}, // onStart callback (not used)
        () => {}, // onEnd callback (not used)
        (id, state) => onUpdateNotification(id, { datetime_dismissed: state })
      );
    } catch (error) {
      await reportAxiosError(error);
    }

    onClose();
    onUpdate();
  };

  // Transform stored_file to uploading document if needed
  const uploadingDocument = reminder.stored_file
    ? getFailedUploadingDocumentFromStoredFile(reminder.stored_file)
    : null;

  const document = (uploadingDocument || reminder.document) as DocumentWithCliveInfo | undefined;

  if (!document) {
    return null;
  }

  // Check for non-dismissed invoice action
  // DocumentWithCliveInfo has index signature [key: string]: unknown, so clive_actions is accessible
  const cliveActions = document.clive_actions as CliveAction[] | undefined;
  const invoiceAction = cliveActions?.find(
    (a) => a.task_type === 'invoice_analysis_recommendation' && a.status === 'pending'
  );

  const handleOpenInvoiceDialog = (action: CliveAction) => {
    setActiveInvoiceAction(action);
    setShowInvoiceDialog(true);
  };

  const handleCloseInvoiceDialog = () => {
    setShowInvoiceDialog(false);
    setActiveInvoiceAction(null);
    onUpdate();
  };

  // If there's an invoice action, show the invoice analysis banner
  if (invoiceAction) {
    return (
      <>
        <div className="my-16">
          <CliveRecommendationPanel
            title="Clive's invoice analysis is ready for review"
            subtitle="Review the extracted invoice data and line items"
            openByDefault={false}
            actionComponent={
              <CliveRecommendationPanelButton text="Review" onClick={() => handleOpenInvoiceDialog(invoiceAction)} />
            }
            avatarType="happy"
          />
        </div>
        {showInvoiceDialog && activeInvoiceAction ? (
          <CliveInvoiceActionContainer
            document={document as unknown as ClaimDocument}
            action={activeInvoiceAction}
            open={showInvoiceDialog}
            onClose={handleCloseInvoiceDialog}
          />
        ) : null}
      </>
    );
  }

  // Get Clive panel content (title, subtitle, avatar type, status flags)
  const clivePanelContent = getClivePanelContent(document);
  const documentSummary = document?.clive_store_information?.full_summary;

  const shouldShowClassifyButton = clivePanelContent.showClassifyButton;

  return (
    <>
      <div className="my-16">
        <CliveRecommendationPanel
          title={clivePanelContent.panelTitle}
          subtitle={clivePanelContent.panelSubtitle}
          openByDefault={false}
          actionComponent={
            shouldShowClassifyButton ? (
              <CliveRecommendationPanelButton text="Classify" onClick={() => setShowEditDocument(true)} />
            ) : undefined
          }
          avatarType={clivePanelContent.avatarType}
        >
          {documentSummary && !clivePanelContent.isUploadFailed && !clivePanelContent.isUnrecognizedType && (
            <CliveSummaryCard title="Document Summary" summaryText={documentSummary} className="m-0" />
          )}
        </CliveRecommendationPanel>
      </div>

      {showEditDocument && shouldShowClassifyButton && (
        <RetryFailedUploadDialog
          open={showEditDocument}
          claim={claim}
          document={document}
          onCancel={() => setShowEditDocument(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </>
  );
}

export default CliveReminderNotificationContent;
